import React, { useEffect, useMemo, useState } from 'react';
import { AdminSidebar } from '../AdminDashboardPage';
import AdminHeader from '../AdminHeader';
import { getAllOrders } from '../../../services/orderService';
import formatPrice from '../../../utils/formatPrice';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import '../../../css/customerReport.css';

import {
  Users,
  UserPlus,
  UserCheck,
  ShoppingBag,
  TrendingUp,
  Wallet,
  UserX,
  FileSpreadsheet,
  Filter,
  Calendar,
} from 'lucide-react';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Match the dashboard's chart typography + tooltip
ChartJS.defaults.font.family = "'Figtree', 'Segoe UI', system-ui, sans-serif";
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = '#8f8178';
Object.assign(ChartJS.defaults.plugins.tooltip, {
  backgroundColor: '#211915',
  titleColor: '#f5ede6',
  bodyColor: '#f5ede6',
  padding: 10,
  cornerRadius: 8,
  displayColors: false,
});

const PALETTE = ['#f4b6c4', '#4a6382', '#2e6a4f', '#c48a2c', '#8a6a9e', '#3d8796', '#d9466f', '#8f8178'];
const GRID_COLOR = '#f0ebe6';
const AXIS_COLOR = '#e7dfd8';

const pieLegend = {
  position: 'bottom',
  labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 8, boxHeight: 8, padding: 16, color: '#4a3d36' },
};

// "Existing customer, no activity" window — a registered customer whose
// most recent order is older than this counts as inactive/lapsed. This is
// independent of the date-range filter above, since it's about lifetime
// order history, not orders within the selected window.
const INACTIVITY_DAYS = 30;

const toISODate = (d) => d.toISOString().slice(0, 10);

const PRESETS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const rangeForPreset = (preset) => {
  const today = new Date();
  const to = toISODate(today);
  if (preset === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return { from: toISODate(from), to };
  }
  if (preset === '30d') {
    const from = new Date(today); from.setDate(from.getDate() - 29);
    return { from: toISODate(from), to };
  }
  if (preset === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toISODate(from), to };
  }
  if (preset === 'all') return { from: '2000-01-01', to };
  return { from: to, to };
};

// Identity key for grouping — registered customers group by user id;
// guests (no user) group by shipping phone, since that's the closest
// thing to a stable identifier we have for a non-account checkout.
const customerKey = (o) => o.user?._id || `guest:${o.shippingAddress?.phone || o._id}`;

const formatDate = (t) => new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const daysSince = (t) => Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));

const CustomerReportPage = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preset, setPreset] = useState('30d');
  const [range, setRange] = useState(rangeForPreset('30d'));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await getAllOrders({});
        setAllOrders(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading orders for customer report:', err);
        setError('Could not load orders.');
        setAllOrders([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(rangeForPreset(key));
  };

  const handleCustomDate = (field, value) => {
    setPreset('custom');
    setRange((prev) => ({ ...prev, [field]: value }));
  };

  // non-cancelled orders only, across all history — used to establish each
  // customer's first-ever order date regardless of the selected range
  const liveOrders = useMemo(
    () => allOrders.filter((o) => !['Cancelled', 'Rejected', 'Returned'].includes(o.status)),
    [allOrders]
  );

  const firstOrderDateByCustomer = useMemo(() => {
    const map = new Map();
    liveOrders.forEach((o) => {
      const key = customerKey(o);
      const t = new Date(o.createdAt).getTime();
      if (!map.has(key) || t < map.get(key)) map.set(key, t);
    });
    return map;
  }, [liveOrders]);

  // Lifetime record per customer, built from ALL history regardless of the
  // date-range filter — this is what "existing customer, no activity in
  // 30 days" needs, since it's about how long it's been since their last
  // order, not whether they ordered inside the currently selected window.
  const lifetimeCustomers = useMemo(() => {
    const map = new Map();
    liveOrders.forEach((o) => {
      const key = customerKey(o);
      const t = new Date(o.createdAt).getTime();
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: o.user?.name || o.shippingAddress?.fullName || 'Guest',
          email: o.user?.email || '',
          phone: o.shippingAddress?.phone || '',
          isGuest: !o.user,
          lifetimeOrders: 0,
          lifetimeSpent: 0,
          lastOrderAt: t,
        });
      }
      const c = map.get(key);
      c.lifetimeOrders += 1;
      c.lifetimeSpent += o.totalPrice || 0;
      if (t > c.lastOrderAt) c.lastOrderAt = t;
    });
    return [...map.values()];
  }, [liveOrders]);

  const inactiveCustomers = useMemo(() => {
    const cutoff = Date.now() - INACTIVITY_DAYS * 24 * 60 * 60 * 1000;
    return lifetimeCustomers
      .filter((c) => !c.isGuest && c.lastOrderAt < cutoff)
      .sort((a, b) => a.lastOrderAt - b.lastOrderAt); // most lapsed first
  }, [lifetimeCustomers]);

  const filtered = useMemo(() => {
    const fromTime = new Date(range.from).setHours(0, 0, 0, 0);
    const toTime = new Date(range.to).setHours(23, 59, 59, 999);
    return liveOrders.filter((o) => {
      const t = new Date(o.createdAt).getTime();
      return t >= fromTime && t <= toTime;
    });
  }, [liveOrders, range]);

  const stats = useMemo(() => {
    const fromTime = new Date(range.from).setHours(0, 0, 0, 0);

    const customerMap = new Map();
    filtered.forEach((o) => {
      const key = customerKey(o);
      const isGuest = !o.user;
      if (!customerMap.has(key)) {
        customerMap.set(key, {
          key,
          name: o.user?.name || o.shippingAddress?.fullName || 'Guest',
          email: o.user?.email || '',
          isGuest,
          orders: 0,
          totalSpent: 0,
        });
      }
      const c = customerMap.get(key);
      c.orders += 1;
      c.totalSpent += o.totalPrice || 0;
    });

    const customers = [...customerMap.values()];
    const registered = customers.filter((c) => !c.isGuest);
    const guests = customers.filter((c) => c.isGuest);

    const newCustomers = registered.filter((c) => {
      const first = firstOrderDateByCustomer.get(c.key);
      return first >= fromTime;
    });
    const returningCustomers = registered.filter((c) => {
      const first = firstOrderDateByCustomer.get(c.key);
      return first < fromTime;
    });

    const guestOrders = filtered.filter((o) => !o.user).length;
    const revenue = filtered.reduce((sum, o) => sum + (o.totalPrice || 0), 0);

    const topCustomers = [...customers]
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 10);

    return {
      totalCustomers: customers.length,
      registeredCount: registered.length,
      guestCount: guests.length,
      newCount: newCustomers.length,
      returningCount: returningCustomers.length,
      guestOrders,
      revenue,
      avgSpendPerCustomer: customers.length ? revenue / customers.length : 0,
      topCustomers,
      typeBreakdown: [
        { type: 'New', count: newCustomers.length },
        { type: 'Returning', count: returningCustomers.length },
        { type: 'Guest', count: guests.length },
      ].filter((t) => t.count > 0),
    };
  }, [filtered, firstOrderDateByCustomer, range]);

  const typeData = {
    labels: stats.typeBreakdown.map((t) => t.type),
    datasets: [{
      data: stats.typeBreakdown.map((t) => t.count),
      backgroundColor: PALETTE,
      borderColor: '#ffffff',
      borderWidth: 3,
      hoverOffset: 4,
    }],
  };

  const topCustomersData = {
    labels: stats.topCustomers.map((c) => c.name),
    datasets: [{
      label: 'Total spent',
      data: stats.topCustomers.map((c) => c.totalSpent),
      backgroundColor: '#f4b6c4',
      hoverBackgroundColor: '#e38aa2',
      borderWidth: 0,
      borderRadius: 6,
      maxBarThickness: 34,
    }],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: pieLegend },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, grid: { color: GRID_COLOR }, border: { display: false } },
      x: { grid: { display: false }, border: { color: AXIS_COLOR } },
    },
  };

  const handleExportCsv = () => {
    const rows = [
      ['Name', 'Email', 'Type', 'Orders', 'Total spent'],
      ...stats.topCustomers.map((c) => [
        c.name, c.email, c.isGuest ? 'Guest' : 'Registered', c.orders, c.totalSpent,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer_report_${range.from}_to_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportInactiveCsv = () => {
    const rows = [
      ['Name', 'Email', 'Phone', 'Last order date', 'Days inactive', 'Lifetime orders', 'Lifetime spent'],
      ...inactiveCustomers.map((c) => [
        c.name, c.email, c.phone, formatDate(c.lastOrderAt), daysSince(c.lastOrderAt), c.lifetimeOrders, c.lifetimeSpent,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inactive_customers_${INACTIVITY_DAYS}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    { key: 'total', label: 'Total customers', value: stats.totalCustomers, icon: Users, note: 'Ordered in selected period' },
    { key: 'new', label: 'New', value: stats.newCount, icon: UserPlus, note: 'First order in this period' },
    { key: 'returning', label: 'Returning', value: stats.returningCount, icon: UserCheck, note: 'Ordered before this period' },
    { key: 'guest', label: 'Guest orders', value: stats.guestOrders, icon: ShoppingBag, note: 'Checked out without an account' },
    { key: 'revenue', label: 'Revenue', value: formatPrice(stats.revenue), icon: TrendingUp, note: 'From customers in period' },
    { key: 'avg', label: 'Avg. spend / customer', value: formatPrice(stats.avgSpendPerCustomer), icon: Wallet, note: 'Revenue per customer' },
    {
      key: 'inactive',
      label: `Inactive ${INACTIVITY_DAYS}+ days`,
      value: inactiveCustomers.length,
      icon: UserX,
      tone: inactiveCustomers.length > 0 ? 'warn' : null,
      note: 'Registered, no recent orders',
    },
  ];

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Customer Report" />

        <div className="cst-page">
          {/* Page heading */}
          <div className="cst-heading">
            <div>
              <h1 className="cst-title">Customer Report</h1>
              <p className="cst-subtitle">New, returning and lapsed customers and their spend</p>
            </div>
            <div className="cst-heading-actions">
              <button
                type="button"
                className="cst-btn cst-btn-primary"
                onClick={handleExportCsv}
                disabled={loading || !stats.topCustomers.length}
              >
                <FileSpreadsheet size={15} />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="cst-filter-bar">
            <div className="cst-filter-left">
              <span className="cst-filter-label">
                <Filter size={13} />
                <span>Period</span>
              </span>
              <div className="cst-presets" role="group" aria-label="Date filter presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`cst-preset-btn ${preset === p.key ? 'active' : ''}`}
                    onClick={() => applyPreset(p.key)}
                    aria-pressed={preset === p.key}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="cst-date-inputs">
              <span className="cst-date-icon">
                <Calendar size={14} />
              </span>
              <input type="date" value={range.from} max={range.to} aria-label="From date" onChange={(e) => handleCustomDate('from', e.target.value)} />
              <span className="cst-date-sep">to</span>
              <input type="date" value={range.to} min={range.from} aria-label="To date" onChange={(e) => handleCustomDate('to', e.target.value)} />
            </div>
          </div>

          {error && <div className="cst-error">{error}</div>}

          {/* Summary cards */}
          <div className="cst-grid">
            {kpis.map(({ key, label, value, icon: Icon, tone, note }) => (
              <div key={key} className={`cst-kpi ${tone && !loading ? `cst-kpi--${tone}` : ''}`}>
                <div className="cst-kpi-top">
                  <span className="cst-kpi-label">{label}</span>
                  <span className="cst-kpi-icon">
                    <Icon size={16} />
                  </span>
                </div>
                <p className="cst-kpi-value">{loading ? '—' : value}</p>
                <span className="cst-kpi-note">{note}</span>
              </div>
            ))}
          </div>

          {/* Customer mix + Top customers chart */}
          <div className="cst-row">
            <section className="cst-panel">
              <header className="cst-panel-head">
                <div>
                  <h3 className="cst-panel-title">Customer mix</h3>
                  <p className="cst-panel-sub">New, returning and guest customers</p>
                </div>
              </header>
              {stats.typeBreakdown.length === 0 && !loading ? (
                <p className="cst-empty">No customers in this range.</p>
              ) : (
                <div className="cst-chart cst-chart--pie">
                  <Pie data={typeData} options={pieOptions} />
                </div>
              )}
            </section>
            <section className="cst-panel">
              <header className="cst-panel-head">
                <div>
                  <h3 className="cst-panel-title">Top customers by spend</h3>
                  <p className="cst-panel-sub">Total spent in the selected period</p>
                </div>
                <span className="cst-tag">Top 10</span>
              </header>
              {stats.topCustomers.length === 0 && !loading ? (
                <p className="cst-empty">No customers in this range.</p>
              ) : (
                <div className="cst-chart">
                  <Bar data={topCustomersData} options={barOptions} />
                </div>
              )}
            </section>
          </div>

          {/* Top customers table */}
          <section className="cst-panel">
            <header className="cst-panel-head">
              <div>
                <h3 className="cst-panel-title">Top 10 customers</h3>
                <p className="cst-panel-sub">Ranked by total spend in the selected period</p>
              </div>
            </header>
            {stats.topCustomers.length === 0 && !loading ? (
              <p className="cst-empty">No customers in this range.</p>
            ) : (
              <div className="cst-table-wrap">
                <table className="cst-table">
                  <thead>
                    <tr>
                      <th className="cst-c-rank">#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th className="cst-num">Orders</th>
                      <th className="cst-num">Total spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topCustomers.map((c, i) => (
                      <tr key={c.key}>
                        <td className="cst-c-rank">
                          <span className={`cst-rank ${i < 3 ? 'cst-rank--top' : ''}`}>{i + 1}</span>
                        </td>
                        <td className="cst-strong">{c.name}</td>
                        <td className="cst-muted">{c.email || '—'}</td>
                        <td>
                          <span className={`cst-pill ${c.isGuest ? 'cst-pill-neutral' : 'cst-pill-accent'}`}>
                            {c.isGuest ? 'Guest' : 'Registered'}
                          </span>
                        </td>
                        <td className="cst-num">{c.orders}</td>
                        <td className="cst-num cst-strong">{formatPrice(c.totalSpent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Existing customers with no orders in the inactivity window —
              lifetime-based, not affected by the date-range filter above */}
          <section className="cst-panel">
            <header className="cst-panel-head">
              <div>
                <h3 className="cst-panel-title">Existing customers, no activity in {INACTIVITY_DAYS}+ days</h3>
                <p className="cst-panel-sub">
                  {inactiveCustomers.length} {inactiveCustomers.length === 1 ? 'customer' : 'customers'} · based on lifetime order history
                </p>
              </div>
              <button
                type="button"
                className="cst-btn cst-btn-secondary cst-btn-sm"
                onClick={handleExportInactiveCsv}
                disabled={loading || !inactiveCustomers.length}
              >
                <FileSpreadsheet size={14} />
                <span>Export CSV</span>
              </button>
            </header>
            {inactiveCustomers.length === 0 && !loading ? (
              <p className="cst-empty">No lapsed customers — everyone with an account has ordered within the last {INACTIVITY_DAYS} days.</p>
            ) : (
              <div className="cst-table-wrap cst-table-wrap--scroll">
                <table className="cst-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Last order</th>
                      <th className="cst-num">Days inactive</th>
                      <th className="cst-num">Lifetime orders</th>
                      <th className="cst-num">Lifetime spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveCustomers.map((c) => (
                      <tr key={c.key}>
                        <td className="cst-strong">{c.name}</td>
                        <td className="cst-muted">{c.email || '—'}</td>
                        <td className="cst-muted">{formatDate(c.lastOrderAt)}</td>
                        <td className="cst-num cst-days-inactive">{daysSince(c.lastOrderAt)}d</td>
                        <td className="cst-num">{c.lifetimeOrders}</td>
                        <td className="cst-num cst-strong">{formatPrice(c.lifetimeSpent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default CustomerReportPage;
